import React, { useState, useEffect } from 'react'
import { useSelector } from 'src/utils/store'
import NumberFormat from 'react-number-format'
import { DarkBlue } from 'src/assets/colors'
import FlexLayout from 'src/components/FlexLayout'
import Text from 'src/components/Text'
import { FractionFieldStates } from '../../FractionUtils'
import { CustomField, FlexDiv } from '../FractionLot/style'
import { InfoCircleFill } from '@styled-icons/bootstrap'
import { Batch, FractionFieldStateEnum, TouchedValues } from '../../FractionTypes'
import { weightFormatter } from 'src/utils'
import { ScaleWeight } from '../ScaleWeight'
import { useDispatch } from 'react-redux'
import { actions } from 'src/features/fraction/FractionState'
import { handleOnKeyDown } from 'src/utils/Form'
import dayjs from 'dayjs'

interface FractionInputProps {
  averageWeight?: number
  values: TouchedValues,
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean | undefined) => void
  errorMessage: JSX.Element
  validateField: (field: string) => void
  batch: Batch
}

const FractionInput = ({
  averageWeight, values, setFieldValue, errorMessage, validateField, batch,
}: FractionInputProps) => {
  const dispatch = useDispatch()
  const [fractionFieldStateEnum, setFractionFieldStateEnum] = useState<FractionFieldStateEnum>(FractionFieldStateEnum.DEFAULT)
  const weight = useSelector((state) => state.fraction.scaleWeight.weight)
  const { isRemotePrinterConnected, fractionTag } = useSelector((state) => state.fraction)

  const compareFractionFieldState = React.useCallback((newFractionState:FractionFieldStateEnum) => {
    if (newFractionState === fractionFieldStateEnum) return
    setFractionFieldStateEnum(newFractionState)
  }, [fractionFieldStateEnum])

  const handleCheckFractionValue = React.useCallback((value: string) => {
    const parsedFractionValue = parseFloat(value)

    if (parsedFractionValue === values.fracao) return

    setFieldValue('fracao', parsedFractionValue, false)

    const parsedFractionValueAsKg = parsedFractionValue / 1000

    dispatch(actions.setScaleWeight({ weight: parsedFractionValue }))
    if (averageWeight) {
      if (!parsedFractionValueAsKg) {
        compareFractionFieldState(FractionFieldStateEnum.DEFAULT)
      } else if (parsedFractionValueAsKg > averageWeight * 1.1) {
        compareFractionFieldState(FractionFieldStateEnum.ABOVE)
      } else if (parsedFractionValueAsKg < averageWeight * 0.9) {
        compareFractionFieldState(FractionFieldStateEnum.BELOW)
      } else {
        compareFractionFieldState(FractionFieldStateEnum.WITHIN)
      }
    }
  }, [averageWeight, compareFractionFieldState, dispatch, setFieldValue, values.fracao])

  const printFractionTag = () => {
    if (isRemotePrinterConnected && fractionTag && batch.fractioning_tag && values.fracao) {
      dispatch(actions.printFractionTag({
        productDescription: batch.descricao,
        productCode: `Código: ${batch.cod_produto}`,
        productBarcode: batch.cod_produto,
        batchId: `Id_lote: ${batch.id_lote}`,
        fractionInclusionDate: `Fracionado: ${dayjs(new Date()).format('DD/MM/YYYY')}`,
        weight: `Peso: ${values.fracao / 1000} Kg`,
      }))
    }
  }

  useEffect(() => {
    if (weight !== values.fracao) {
      handleCheckFractionValue(weight?.toString() || '')
    }
  }, [weight, handleCheckFractionValue, values.fracao])

  const resolveAverageWeightIndicator = React.useMemo(() => averageWeight && (
    <>
      <InfoCircleFill color={DarkBlue} size={9} />
      <Text textType='label' testId='text-indicator' text={`A fração indicada para esse produto é de ${weightFormatter(averageWeight * 1000)} kg.`} />
    </>
  ), [averageWeight])

  const fractionFieldState = React.useMemo(() => FractionFieldStates[fractionFieldStateEnum], [fractionFieldStateEnum])

  return (
    <FlexLayout style={{ margin: '15px 5px 0 0' }} justify='center'>
      <FlexLayout direction='column'>
        <FlexDiv justify='space-between' align='center' >
          <Text textType='defaut' text='fraction.form.value' />

          <FlexDiv align='center' gap='3px'>
            {resolveAverageWeightIndicator}
          </FlexDiv>
        </FlexDiv>
        <NumberFormat
          id='fracao'
          name='fracao'
          placeholder='Pressione barra de espaço para pesar'
          decimalSeparator=','
          thousandSeparator='.'
          allowLeadingZeros={false}
          allowNegative={false}
          fixedDecimalScale
          decimalScale={3}
          allowEmptyFormatting={false}
          defaultValue=''
          value={values.fracao}
          onChange={() => { validateField('fracao') }}
          onValueChange={(e) => {
            handleCheckFractionValue(e.value)
          }}
          format={(value) => weightFormatter(value)}
          onKeyDown={(e) => handleOnKeyDown<HTMLInputElement>(e, 'barcodeProduct', printFractionTag)}
          style={{ ...CustomField, borderBottom: `3px solid ${fractionFieldState.color}` }}
          maxLength={10}
          data-testid='input-fraction'
        />

        <FlexLayout justify='between' align='start' style={{ margin: '0px 0 6px 0' }}>
          {errorMessage}
          <FlexDiv align='center' gap='6px' style={{ margin: '10px 0 0 0' }}>
            {fractionFieldState.icon}
            <Text testId='text-message' textType='defaut' text={fractionFieldState.message} />
          </FlexDiv>
          <ScaleWeight nextField='barcodeProduct' batch={batch} />
        </FlexLayout>
      </FlexLayout>
    </FlexLayout>
  )
}

export default React.memo(FractionInput)
